import badgeLogoMask from 'public/assets/icons/header-logo.svg';
import cardSurface from 'public/assets/images/scratch/card-surface.webp';
import footerEight from 'public/assets/images/scratch/footer-eight.webp';
import camo1 from 'public/assets/images/scratch/background/camo-1.webp';
import camo2 from 'public/assets/images/scratch/background/camo-2.webp';
import camo3 from 'public/assets/images/scratch/background/camo-3.webp';
import camo4 from 'public/assets/images/scratch/background/camo-4.webp';
import camo5 from 'public/assets/images/scratch/background/camo-5.webp';
import camo6 from 'public/assets/images/scratch/background/camo-6.webp';
import camo7 from 'public/assets/images/scratch/background/camo-7.webp';
import camo8 from 'public/assets/images/scratch/background/camo-8.webp';
import hero1Dropday from 'public/assets/images/scratch/dropday/1-Dropday.webp';
import hero1Lucky8s from 'public/assets/images/scratch/lucky/1-Lucky.webp';
import hero1SpecialPrices from 'public/assets/images/scratch/special/1-Special.webp';
import hero2Dropday from 'public/assets/images/scratch/dropday/2-Dropday.webp';
import hero2Lucky8s from 'public/assets/images/scratch/lucky/2-Lucky.webp';
import hero2SpecialPrices from 'public/assets/images/scratch/special/2-Special.webp';
import hero3Dropday from 'public/assets/images/scratch/dropday/3-Dropday.webp';
import hero3Lucky8s from 'public/assets/images/scratch/lucky/3-Lucky.webp';
import hero3SpecialPrices from 'public/assets/images/scratch/special/3-Special.webp';
import hero4Dropday from 'public/assets/images/scratch/dropday/4-Dropday.webp';
import hero4Lucky8s from 'public/assets/images/scratch/lucky/4-Lucky.webp';
import hero4SpecialPrices from 'public/assets/images/scratch/special/4-Special.webp';
import hero5Dropday from 'public/assets/images/scratch/dropday/5-Dropday.webp';
import hero5Lucky8s from 'public/assets/images/scratch/lucky/5-Lucky.webp';
import hero5SpecialPrices from 'public/assets/images/scratch/special/5-Special.webp';
import hero6Dropday from 'public/assets/images/scratch/dropday/6-Dropday.webp';
import hero6Lucky8s from 'public/assets/images/scratch/lucky/6-Lucky.webp';
import hero6SpecialPrices from 'public/assets/images/scratch/special/6-Special.webp';
import hero7Dropday from 'public/assets/images/scratch/dropday/7-Dropday.webp';
import hero7Lucky8s from 'public/assets/images/scratch/lucky/7-Lucky.webp';
import hero7SpecialPrices from 'public/assets/images/scratch/special/7-Special.webp';
import hero8Dropday from 'public/assets/images/scratch/dropday/8-Dropday.webp';
import hero8Lucky8s from 'public/assets/images/scratch/lucky/8-Lucky.webp';
import hero8SpecialPrices from 'public/assets/images/scratch/special/8-Special.webp';
import {
  SCRATCH_DESIGN_IDS,
  type ScratchDesignId,
} from '~/common/constants/scratchDrop';

/**
 * The eight Scratch Drop colourways (BPES-237).
 *
 * Layout is identical across all eight; only the camo, the three hero graphics and a few
 * colours change. Assets are imported as modules from a `public/...` specifier so esbuild
 * content-hashes them and a swap cannot serve a stale cached copy.
 *
 * Every design's URLs land in the chunk, but they are strings, not bytes — the browser
 * fetches only the active colourway. Do NOT switch to per-design dynamic imports; that puts
 * the LCP background behind a JS round-trip on a page that exists for one hour.
 */

/**
 * Where a hero graphic hangs off the card. Its centre sits on a corner or edge midpoint, so
 * exactly half hangs outside — a pure translate, needing no measured coordinates.
 */
export type ScratchHeroAnchor = 'topLeft' | 'top' | 'topRight';

/**
 * Hero widths resolve against `ScratchCardFrame`, which has no border or padding of its own,
 * so its padding box is exactly the card's border box: 792px, the 952px Figma column minus
 * its 80px side padding.
 */
export const SCRATCH_CARD_REFERENCE_WIDTH = 792;

/**
 * The mobile card, 343px on a 375px screen -- the screen less ScratchInner's 16px gutter
 * each side. Was 335 (the flat Figma frame) until the card went full bleed.
 *
 * This is the number the mobile artwork was exported against: the Lucky 8s asset is exactly
 * 343px wide, i.e. the full card, which is what makes it render at native size on a 375px
 * screen and scale from there.
 */
export const SCRATCH_CARD_REFERENCE_WIDTH_MOBILE = 343;

/** Design px -> a percentage of the card, so the artwork scales with it. */
export const heroWidth = (px: number, reference: number): string =>
  `${((px / reference) * 100).toFixed(2)}%`;

export interface ScratchHeroArt {
  src: string;
  /** Empty string marks the graphic as decorative. */
  alt: string;
  anchor: ScratchHeroAnchor;
  /**
   * The mobile design reorders the graphics rather than just shrinking them: Dropday moves
   * centre to left, Special prices left to right, Lucky 8s takes the middle.
   */
  mobileAnchor: ScratchHeroAnchor;
  /**
   * CSS width in design px, against a 792px card. `heroWidth` converts it to a percentage
   * at render time, so the artwork scales with the card rather than pinning to a fixed size.
   *
   * This is the UNROTATED width. Where `rotation` is set the rendered bounding box is wider
   * than this -- a rotated box measures `w * cos(r) + h * sin(r)` -- so these values are
   * back-computed from the size the design calls for:
   *
   *   lucky 8s        410px at 8.63deg   -> 377.3
   *   special prices  330px at 34.79deg  -> 261.5
   *   dropday         310px, no rotation -> 310
   *
   * Recompute if a `rotation`, an asset's aspect ratio, or the design size changes. Getting
   * this wrong is not subtle: carrying the old baked-in-rotation widths across to the new
   * unrotated assets rendered special prices 28% oversized.
   */
  width: number;
  /**
   * Width at and below `sm`, against `SCRATCH_CARD_REFERENCE_WIDTH_MOBILE`. These are the
   * mobile assets' own pixel dimensions, so at a 375px screen each renders 1:1.
   */
  mobileWidth: number;
  /**
   * Extra rotation in degrees, applied ABOVE sm only.
   *
   * The assets ship with the design's own rotation baked into the pixels, so this is an
   * additional tilt on top of that, not the graphic's own angle. Mobile is excluded
   * structurally rather than by a flag: ANCHOR_POSITION_SM writes its own transform with no
   * rotate term, so it cannot inherit one.
   *
   * Declared per graphic rather than per anchor for the same reason as mobileAboveCard --
   * moving a graphic to a different corner must not silently change how it is drawn.
   */
  rotation?: number;
  /**
   * Paints above the card at `sm` instead of behind it. Declared per graphic rather than
   * derived from its anchor, so moving one to a different corner cannot silently change
   * which layer it lands on.
   */
  mobileAboveCard?: boolean;
}

/**
 * Byte-identical across all eight designs, verified by md5. The card surface is three Figma
 * layers (white plus two textures at 40% and 20%) pre-composited offline: 1.18MB and two
 * runtime opacity layers became 42KB and none. The badge wordmark is a CSS mask, tinted per
 * design.
 */
export const SCRATCH_SHARED_ASSETS = {
  cardSurface,
  footerEight,
  badgeLogoMask,
} as const;

interface ScratchPalette {
  /** The "8 DROPS — NEW DROP EVERY HOUR…" line. */
  footerPrimary: string;
  /** The free-shipping line. */
  footerShipping: string;
  /** Tint for the card's wordmark badge. */
  badge: string;
  /**
   * The badge's glow. A LIGHTER TINT of the accent, not the accent itself -- six of the
   * eight designs give it a different value, so it cannot be derived from the accent.
   */
  glow: string;
}

export interface ScratchDesign {
  id: ScratchDesignId;
  camo: string;
  hero: ScratchHeroArt[];
  palette: ScratchPalette;
}

/**
 * Geometry is shared by all eight designs — only the artwork differs — so it is declared
 * once here and each design supplies three images and three colours.
 *
 * Painted in array order, so Lucky 8s is first and therefore behind the other two, which is
 * what the mobile design needs where the wordmarks tuck over the big 8.
 *
 * Widths are the assets' own dimensions: Figma's rotated bounding boxes, since rotation is
 * baked into every file. Positions are not measured at all — the anchors place them.
 */
const heroFor = (
  lucky8s: string,
  dropday: string,
  specialPrices: string,
): ScratchHeroArt[] => [
  {
    // Decorative: "Lucky 8s" repeats the campaign name the other two already carry.
    src: lucky8s,
    alt: '',
    anchor: 'topRight',
    mobileAnchor: 'top',
    width: 377.3,
    // 343 is the full width of the mobile card, which is how the asset was exported.
    // Not back-computed like `width`: mobile takes no rotation, so it needs no correction.
    mobileWidth: 343,
    rotation: 8.63,
  },
  {
    src: dropday,
    alt: 'Scratch Dropday',
    anchor: 'top',
    mobileAnchor: 'topLeft',
    width: 310,
    mobileWidth: 155,
  },
  {
    src: specialPrices,
    alt: 'Special prices!',
    anchor: 'topLeft',
    mobileAnchor: 'topRight',
    width: 261.5,
    mobileWidth: 86,
    rotation: -34.79,
    mobileAboveCard: true,
  },
];

/**
 * Figma exposes no variables (`get_variable_defs` returns `{}` on every node), so these are
 * hand-transcribed. Designs 1 and 2 came off the Figma layers; 3-8 were sampled from the
 * dominant accent in each design's own Lucky 8s artwork — the method reproduces design 1's
 * #fceb8a and design 2's #a4daeb to within two points, but they are DERIVED, not authored.
 * Worth a designer's eye before launch.
 */
/**
 * Colours are passed by NAME, not position. There are three, two of them are usually the
 * same value and all three are hex strings -- a positional signature would be one
 * transposition away from a wrong colour that typechecks and looks plausible.
 */
interface ScratchColours {
  /** The badge tint, and the free-shipping line unless overridden below. */
  accent: string;
  /** The badge's glow; see ScratchPalette. */
  glow: string;
  /**
   * Overrides the free-shipping line alone, leaving the badge on `accent`. Design 7 is the
   * only user: the client asked for a softer pink on that line, and folding it into the
   * accent would have re-tinted the badge as a side effect.
   */
  footerShipping?: string;
}

const design = (
  id: ScratchDesignId,
  camo: string,
  hero: ScratchHeroArt[],
  { accent, glow, footerShipping = accent }: ScratchColours,
): ScratchDesign => ({
  id,
  camo,
  hero,
  palette: { footerPrimary: '#ffffff', footerShipping, badge: accent, glow },
});

const SCRATCH_DESIGNS: Record<ScratchDesignId, ScratchDesign> = {
  1: design(1, camo1, heroFor(hero1Lucky8s, hero1Dropday, hero1SpecialPrices), {
    accent: '#fceb8a',
    glow: '#fceb8a',
  }),
  2: design(2, camo2, heroFor(hero2Lucky8s, hero2Dropday, hero2SpecialPrices), {
    accent: '#a4daeb',
    glow: '#a4daeb',
  }),
  3: design(3, camo3, heroFor(hero3Lucky8s, hero3Dropday, hero3SpecialPrices), {
    accent: '#d7c5d7',
    glow: '#d7c4d7',
  }),
  4: design(4, camo4, heroFor(hero4Lucky8s, hero4Dropday, hero4SpecialPrices), {
    accent: '#77bcd9',
    glow: '#b5dff7',
  }),
  5: design(5, camo5, heroFor(hero5Lucky8s, hero5Dropday, hero5SpecialPrices), {
    accent: '#d694ab',
    glow: '#f1c5d2',
  }),
  6: design(6, camo6, heroFor(hero6Lucky8s, hero6Dropday, hero6SpecialPrices), {
    accent: '#9be397',
    glow: '#c1f2c1',
  }),
  7: design(7, camo7, heroFor(hero7Lucky8s, hero7Dropday, hero7SpecialPrices), {
    accent: '#fe77a4',
    glow: '#ffb3c4',
    footerShipping: '#eebad2',
  }),
  8: design(8, camo8, heroFor(hero8Lucky8s, hero8Dropday, hero8SpecialPrices), {
    accent: '#c7a7d6',
    glow: '#e0cbe7',
  }),
};

export const getScratchDesign = (id: ScratchDesignId): ScratchDesign =>
  SCRATCH_DESIGNS[id] ?? SCRATCH_DESIGNS[SCRATCH_DESIGN_IDS[0]];
